import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import {
    getUfoSubstepForPolling,
    ProductKey,
    ufoEventAsyncArchitecturePollingStatus,
    ufoEventConfirmationPageStatusPolling,
    ufoEventRedirectToProduct
} from "@/components/signup/utils";
import {
    IBxpWebStargateClient,
    ProductActivationStatusResponse
} from "@/libraries/bxp-web-stargate-client/src";

import { trackGetProvisioningStatusLatencyEvent } from "../../utils/analytics";
import { ConfirmationPageType } from "../use-confirmation-page-type";

import { DEFAULT_TIMEOUT } from "./types";

export type PollingCallbacks<T = boolean> = {
    onSuccess: (startTime: number, result: T) => void;
    onError: (startTime: number, error: unknown) => void;
    onTimeout?: (startTime: number) => void;
};

export type PollingOptions<T = boolean> = {
    checkFn: () => Promise<T>;
    callbacks: PollingCallbacks<T>;
    timeout?: number;
    isReady?: (result: T) => boolean;
};

/**
 * Generic polling function that can be used with any promise-returning function.
 * Implements the same polling strategy as pollCloudStatus:
 * - Initial run: check immediately
 * - Run 1: wait 15 seconds
 * - Runs 2-9: poll every 2 seconds (fast polling)
 * - Runs 10+: poll every 10 seconds (slow polling)
 *
 * @param options - Polling configuration options
 * @returns A function that starts polling when called
 */
export const createPollingFunction = <T = boolean>({
    checkFn,
    callbacks,
    timeout = DEFAULT_TIMEOUT,
    isReady = (result: T) => Boolean(result)
}: PollingOptions<T>) => {
    const pollFunction = (startTime = Date.now(), run = 0) => {
        const poll = () => {
            const endTime = Date.now();
            const elapsedTime = endTime - startTime;

            if (elapsedTime > timeout) {
                callbacks.onTimeout?.(startTime);
                return;
            }

            checkFn()
                .then((result) => {
                    if (isReady(result)) {
                        callbacks.onSuccess(startTime, result);
                    } else {
                        pollFunction(startTime, run + 1);
                    }
                })
                .catch((error) => {
                    callbacks.onError(startTime, error);
                });
        };

        // initial run, check immediately to avoid rate limiting issues
        if (run === 0) {
            poll();
            return;
        }
        // we expect the majority of requests to be completed in the 15-30 second range, wait until 15 seconds to begin fast polling
        if (run === 1) {
            setTimeout(() => poll(), 15000);
            return;
        }
        // in the 15-30 seconds range, fast poll every 2 seconds
        if (run > 1 && run < 10) {
            setTimeout(() => poll(), 2000);
            return;
        }
        // if provisioning is delayed, slow poll every 10 seconds until the timeout if necessary
        setTimeout(() => poll(), 10000);
    };

    return pollFunction;
};

export const getCloudIdFromProductActivationStatuses = (
    ccpResponses: ProductActivationStatusResponse[]
): string | undefined =>
    ccpResponses
        .map(({ resourcePackages }) =>
            resourcePackages
                .map(
                    ({ resourceAris }) =>
                        resourceAris
                            .find((resourceAri) =>
                                resourceAri.startsWith(
                                    "ari:cloud:platform::site/"
                                )
                            )
                            ?.split("/")[1]
                )
                .find((e) => e)
        )
        .find((e) => e);

export const getPrimaryProduct = (products: readonly string[]) => {
    const filteredProducts = products.filter(
        (product) => product !== ProductKey.JIRA_SOFTWARE
    );
    if (filteredProducts.length > 0 && filteredProducts[0] !== "") {
        return filteredProducts[0];
    }
    if (products.length > 0 && products[0] !== "") {
        return products[0];
    }
};

type ProgressBarStatusProps = {
    readonly startTime: number;
    readonly pivotPercentage: number;
    readonly progressBarPivotPoint: number;
    readonly slowProgressIncrement: number;
    readonly slowProgress: number;
    readonly interval?: NodeJS.Timer;
};
export function getProgressbarStatus({
    startTime,
    pivotPercentage,
    progressBarPivotPoint,
    slowProgressIncrement,
    slowProgress,
    interval
}: ProgressBarStatusProps) {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= DEFAULT_TIMEOUT) {
        // @ts-ignore
        clearInterval(interval);
        return 1;
    } else if (elapsedTime <= progressBarPivotPoint) {
        const rapidProgress =
            (pivotPercentage / progressBarPivotPoint) * elapsedTime;
        return rapidProgress;
    } else {
        slowProgress =
            slowProgress === 0
                ? pivotPercentage
                : slowProgress + slowProgressIncrement;
        return slowProgress;
    }
}
export type FireUfoEventForPollingResultProps = {
    pollingResult: "success" | "error" | "timeout";
    elapsedTime: number;
    platform: string | undefined;
    productKey: string;
    edition: string | undefined;
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    currentScreen: ConfirmationPageType;
    workflowId: string;
    error?: unknown;
};
export const fireUfoEventForPollingResult = ({
    pollingResult,
    elapsedTime,
    platform,
    productKey,
    edition,
    createAnalyticsEvent,
    currentScreen,
    workflowId,
    error
}: FireUfoEventForPollingResultProps): void => {
    try {
        const { timingKey, requestPath } = getUfoSubstepForPolling(platform);

        const errorOrigin = error
            ? `CloudStatusProvider polling ${pollingResult}`
            : undefined;

        // Performance metrics for polling duration
        ufoEventRedirectToProduct.endSubstep(timingKey, {
            ...(error ? { error } : {}),
            customData: {
                reqPath: requestPath,
                elapsedTimeMs: elapsedTime,
                workflowId,
                ...(errorOrigin ? { errorOrigin } : {})
            }
        });

        const ufoEvent = workflowId
            ? ufoEventAsyncArchitecturePollingStatus
            : ufoEventConfirmationPageStatusPolling;

        if (pollingResult === "success") {
            // SLO "good event" for cloud status polling
            ufoEvent.success({
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "succeeded",
                    reqPath: requestPath,
                    elapsedTimeMs: elapsedTime,
                    workflowId
                }
            });
            fireFrontendProvisioningCompletedEvent(
                "polling",
                productKey,
                edition,
                createAnalyticsEvent,
                workflowId
            );
        } else if (pollingResult === "error") {
            // SLO "bad event" for cloud status polling
            ufoEvent.failure({
                error,
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "errored",
                    reqPath: requestPath,
                    elapsedTimeMs: elapsedTime,
                    errorOrigin,
                    workflowId
                }
            });
        } else if (pollingResult === "timeout") {
            // SLO "bad event" for provisioning status
            ufoEvent.failure({
                error,
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "timedOut",
                    elapsedTimeMs: elapsedTime,
                    errorOrigin,
                    workflowId
                }
            });
        }

        // GasV3 event to measure polling provisioning latency
        trackGetProvisioningStatusLatencyEvent({
            createAnalyticsEvent,
            productKey,
            provisioningScreenOnComplete: currentScreen,
            fetchType: "polling",
            elapsedTimeSeconds: Math.round(elapsedTime / 1000),
            workflowId
        });
    } catch (error) {
        console.error(error);
    }
};
export const fireFrontendProvisioningCompletedEvent = (
    fetchType: string,
    productKey = "no-product-key",
    edition = "no-edition",
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    workflowId: string
) => {
    const startTimeStr = sessionStorage.getItem("provisioningStartTime");

    if (startTimeStr && Number(startTimeStr)) {
        const startTimeSeconds = Number(startTimeStr) / 1000;
        const endTimeSeconds = Date.now() / 1000;
        const durationSeconds = endTimeSeconds - startTimeSeconds;
        createAnalyticsEvent({
            eventType: "track",
            action: "redirected",
            actionSubject: "frontend-provisioning-time",
            attributes: {
                startTimeSeconds,
                endTimeSeconds,
                productKey,
                edition,
                durationSeconds,
                fetchType,
                workflowId
            }
        }).fire();
    }
};
export const getAsyncActivationStatus = async (
    stargateClient: IBxpWebStargateClient,
    workflowId: string
) => {
    const response = await stargateClient.asyncActivationStatus({
        workflowId
    });

    if (response.workflowStatus === "PROVISIONING_SUCCEEDED") {
        return true;
    }

    if (response.workflowStatus === "UNRECOVERABLE_ERROR") {
        throw new Error("Activation failed");
    }

    return false;
};

export const checkForPrimaryPackageStatus = (
    redirectProductIdentifier: string,
    requestIds: ProductActivationStatusResponse[],
    cloudId: string
) => {
    const matchingResourcePackage = requestIds
        .flatMap((r) => r.resourcePackages)
        .find((p) =>
            p.resourceAris.some((a) => a.includes(redirectProductIdentifier))
        );

    // If no matching resource package, check all packages status
    if (!matchingResourcePackage) {
        return checkAllPackagesStatus(requestIds, cloudId);
    }

    const status = matchingResourcePackage.action.status;

    if (status === "PROVISIONING_FAILURE") {
        ufoEventRedirectToProduct.setCustomData({
            activationStatus: "PROVISIONING_FAILURE"
        });
        return Promise.reject(
            new Error(
                `Provisioning Failure CCP with cloudId ${cloudId}: ${JSON.stringify(
                    matchingResourcePackage.action.errorDetails
                )}`
            )
        );
    } else if (status === "SUCCESS") {
        return Promise.resolve(true);
    }

    return Promise.resolve(false);
};

export const checkAllPackagesStatus = (
    requestIds: ProductActivationStatusResponse[],
    cloudId: string
) => {
    for (const resource of requestIds) {
        if (
            resource.resourcePackages.find(
                (item) => item.action.status === "IN_PROGRESS"
            )
        )
            return Promise.resolve(false);

        if (
            resource.resourcePackages.find(
                (item) => item.action.status === "PROVISIONING_FAILURE"
            )
        ) {
            ufoEventRedirectToProduct.setCustomData({
                activationStatus: "PROVISIONING_FAILURE"
            });
            return Promise.reject(
                new Error(
                    `Provisioning Failure CCP with cloudId ${cloudId}: ${JSON.stringify(
                        resource.resourcePackages[0]?.action.errorDetails
                    )}`
                )
            );
        }
    }

    return Promise.resolve(true);
};
